/**
 * Atlas PM · 权限配置 · 接口封装（P4.4）
 *
 * 对接后端 RoleController（apps/api/src/modules/role/role.controller.ts）：
 *   GET /system/roles                        角色列表（含各自权限点 code 数组）
 *   GET /system/permissions                  全部权限点（按 category 分组）
 *   PUT /system/roles/:id/permissions        更新某角色权限点集合
 *
 * ⚠️ client.ts 会自动解包响应的顶层 `{ data }`，因此这里直接拿到内层结构。
 * ⚠️ 错误码（前端友好提示）：
 *   ROLE_BUILTIN_PROTECTED  超管不可改 → 勾选框置灰
 *   PERM_ESCALATION         不能授予自己没有的权限
 *   PERM_NO_SYSTEM_CONFIG   会导致无人能进系统设置
 *   PERM_CODE_INVALID       权限点无效
 */

import { api } from '../../../api/client';

/* ── 领域类型（与后端 role.service.ts 的 DTO 对齐） ── */

export interface RoleItem {
  id: string;
  code: string;
  name: string;
  scopeType: number;
  isBuiltin: boolean;
  /** 该角色当前拥有的权限点 code（升序） */
  permissions: string[];
}

export interface PermissionItem {
  code: string;
  resource: string;
  action: string;
}

export interface PermissionGroup {
  category: string;
  permissions: PermissionItem[];
}

export interface PermissionCatalog {
  groups: PermissionGroup[];
  total: number;
}

export interface UpdateRolePermissionsResult {
  id: string;
  code: string;
  permissions: string[];
  before: string[];
}

/** 受保护角色：权限不可通过接口修改 */
export const PROTECTED_ROLE_CODE = 'SUPER_ADMIN';

/** 该角色是否为受保护（超管）角色 */
export function isProtectedRole(role: Pick<RoleItem, 'isBuiltin' | 'code'>): boolean {
  return role.isBuiltin && role.code === PROTECTED_ROLE_CODE;
}

/**
 * 已知错误码 → 更贴合场景的中文前缀（message 仍保留后端原文）。
 * 后端 message 已是中文，这里只做兜底。
 */
export const ROLE_ERROR_LABEL: Record<string, string> = {
  ROLE_BUILTIN_PROTECTED: '该角色受保护',
  PERM_ESCALATION: '提权被拒绝',
  PERM_NO_SYSTEM_CONFIG: '系统设置可达性保护',
  PERM_CODE_INVALID: '权限点无效',
};

export const roleApi = {
  listRoles: () => api.get<{ items: RoleItem[] }>('/system/roles'),

  listPermissions: () => api.get<PermissionCatalog>('/system/permissions'),

  updatePermissions: (roleId: string, permissionCodes: string[]) =>
    api.put<UpdateRolePermissionsResult>(`/system/roles/${roleId}/permissions`, {
      permissionCodes,
    }),
};
